/**
 * ResourceGraph -- interactive SVG topology graph of Pulumi resources.
 *
 * Pure SVG rendering with:
 * - Hierarchical tree layout (root -> providers -> resources -> children)
 * - Provider color-coded accent bars
 * - Status indicators (created, updated, deleted, unchanged, in-progress)
 * - Click to select / expand details
 * - Zoom and pan via SVG viewBox manipulation
 * - Hover tooltips
 * - Empty state
 * - Responsive container
 */

import {
  Component,
  createMemo,
  createSignal,
  For,
  Show,
  createEffect,
  on,
} from "solid-js";

import type { PulumiResource, ResourceGraphProps, ResolvedEdge } from "./types";
import { computeLayout, DEFAULT_LAYOUT_CONFIG } from "./layout";
import { ResourceNode } from "./ResourceNode";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MIN_ZOOM = 0.3;
const MAX_ZOOM = 2.0;
const ZOOM_STEP = 0.15;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ResourceGraph: Component<ResourceGraphProps> = (props) => {
  // --- State ---
  const [selectedUrn, setSelectedUrn] = createSignal<string | null>(
    props.selectedResourceUrn ?? null,
  );
  const [zoom, setZoom] = createSignal(1);
  const [panX, setPanX] = createSignal(0);
  const [panY, setPanY] = createSignal(0);
  const [dragging, setDragging] = createSignal(false);
  const [dragStart, setDragStart] = createSignal({ x: 0, y: 0 });

  // Sync controlled `selectedResourceUrn` prop
  createEffect(
    on(
      () => props.selectedResourceUrn,
      (urn) => {
        if (urn !== undefined) setSelectedUrn(urn ?? null);
      },
    ),
  );

  // --- Layout ---
  const containerHeight = () => props.height ?? 500;
  const showStatus = () => props.showStatus !== false;

  const layout = createMemo(() => computeLayout(props.resources, DEFAULT_LAYOUT_CONFIG));

  const nodeList = createMemo(() => Array.from(layout().nodes.values()));
  const resolvedEdges = createMemo(() => layout().resolvedEdges);

  const graphWidth = createMemo(() => Math.max(layout().width, 400));
  const graphHeight = createMemo(() => Math.max(layout().height, 300));

  // --- viewBox computed from zoom + pan ---
  const viewBox = createMemo(() => {
    const z = zoom();
    const vw = graphWidth() / z;
    const vh = graphHeight() / z;
    const vx = -panX() / z;
    const vy = -panY() / z;
    return `${vx} ${vy} ${vw} ${vh}`;
  });

  // --- Handlers ---
  const handleNodeClick = (resource: PulumiResource) => {
    setSelectedUrn((prev) => (prev === resource.urn ? null : resource.urn));
    props.onResourceClick?.(resource);
  };

  const handleZoomIn = () => setZoom((z) => Math.min(z + ZOOM_STEP, MAX_ZOOM));
  const handleZoomOut = () => setZoom((z) => Math.max(z - ZOOM_STEP, MIN_ZOOM));
  const handleResetView = () => {
    setZoom(1);
    setPanX(0);
    setPanY(0);
  };

  const handleWheel = (e: WheelEvent) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      handleZoomIn();
    } else {
      handleZoomOut();
    }
  };

  const handleMouseDown = (e: MouseEvent) => {
    // Only pan on middle-click or when holding space (via the container)
    if (e.button === 1 || e.button === 0) {
      setDragging(true);
      setDragStart({ x: e.clientX - panX(), y: e.clientY - panY() });
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!dragging()) return;
    setPanX(e.clientX - dragStart().x);
    setPanY(e.clientY - dragStart().y);
  };

  const handleMouseUp = () => {
    setDragging(false);
  };

  /**
   * Build the SVG path for an edge using a cubic bezier curve.
   */
  const edgePath = (edge: ResolvedEdge): string => {
    const midY = (edge.sourceY + edge.targetY) / 2;
    return `M ${edge.sourceX} ${edge.sourceY} C ${edge.sourceX} ${midY}, ${edge.targetX} ${midY}, ${edge.targetX} ${edge.targetY}`;
  };

  return (
    <div
      class="resource-graph-container"
      data-testid="resource-graph"
      style={{
        position: "relative",
        width: "100%",
        "user-select": dragging() ? "none" : "auto",
      }}
    >
      {/* Toolbar */}
      <div
        class="resource-graph-toolbar"
        style={{
          display: "flex",
          "align-items": "center",
          gap: "8px",
          "margin-bottom": "8px",
        }}
      >
        <button
          type="button"
          onClick={handleZoomIn}
          title="Zoom in"
          data-testid="zoom-in"
          class="resource-graph-btn"
        >
          +
        </button>
        <button
          type="button"
          onClick={handleZoomOut}
          title="Zoom out"
          data-testid="zoom-out"
          class="resource-graph-btn"
        >
          -
        </button>
        <button
          type="button"
          onClick={handleResetView}
          title="Reset view"
          data-testid="zoom-reset"
          class="resource-graph-btn"
        >
          Reset
        </button>
        <span
          class="resource-graph-meta"
          data-testid="resource-count"
          style={{ "margin-left": "8px", "font-size": "12px", opacity: "0.7" }}
        >
          {props.resources.length} resources | {Math.round(zoom() * 100)}%
        </span>
      </div>

      {/* SVG canvas */}
      <div
        class="resource-graph-canvas"
        style={{
          border: "1px solid var(--border-primary, #374151)",
          "border-radius": "8px",
          overflow: "hidden",
          background: "var(--bg-secondary, #111827)",
          height: `${containerHeight()}px`,
          cursor: dragging() ? "grabbing" : "grab",
        }}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <Show
          when={props.resources.length > 0}
          fallback={
            <div
              data-testid="empty-state"
              role="status"
              style={{
                display: "flex",
                "align-items": "center",
                "justify-content": "center",
                height: "100%",
                color: "var(--text-tertiary, #6B7280)",
                "font-size": "14px",
              }}
            >
              No resources to display
            </div>
          }
        >
          <svg
            width="100%"
            height="100%"
            viewBox={viewBox()}
            preserveAspectRatio="xMidYMid meet"
            data-testid="resource-graph-svg"
          >
            {/* Edges (drawn first, behind nodes) */}
            <g data-testid="edges-group">
              <For each={resolvedEdges()}>
                {(edge) => (
                  <path
                    d={edgePath(edge)}
                    fill="none"
                    stroke={
                      edge.type === "dependency"
                        ? "var(--text-tertiary, #6B7280)"
                        : "var(--text-secondary, #9CA3AF)"
                    }
                    stroke-width={edge.type === "dependency" ? "1" : "1.5"}
                    stroke-dasharray={edge.type === "dependency" ? "6,4" : "none"}
                    opacity="0.6"
                    data-testid={`edge-${edge.source}-${edge.target}`}
                  />
                )}
              </For>
            </g>

            {/* Nodes */}
            <g data-testid="nodes-group">
              <For each={nodeList()}>
                {(node) => (
                  <ResourceNode
                    resource={node.resource}
                    x={node.x}
                    y={node.y}
                    width={DEFAULT_LAYOUT_CONFIG.nodeWidth}
                    height={DEFAULT_LAYOUT_CONFIG.nodeHeight}
                    selected={selectedUrn() === node.resource.urn}
                    onClick={handleNodeClick}
                    showStatus={showStatus()}
                  />
                )}
              </For>
            </g>
          </svg>
        </Show>
      </div>

      {/* Legend */}
      <div
        class="resource-graph-legend"
        style={{
          display: "flex",
          "flex-wrap": "wrap",
          gap: "12px",
          "margin-top": "8px",
          "font-size": "11px",
          opacity: "0.8",
        }}
      >
        <Show when={showStatus()}>
          <LegendItem color="#34D399" label="Created" />
          <LegendItem color="#FBBF24" label="Updated" />
          <LegendItem color="#F87171" label="Deleted" />
          <LegendItem color="#D1D5DB" label="Unchanged" />
          <LegendItem color="#60A5FA" label="Creating" />
          <LegendItem color="#9CA3AF" label="Pending" />
        </Show>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Legend helper
// ---------------------------------------------------------------------------

const LegendItem: Component<{ color: string; label: string }> = (props) => (
  <span style={{ display: "inline-flex", "align-items": "center", gap: "4px" }}>
    <span
      style={{
        display: "inline-block",
        width: "10px",
        height: "10px",
        "border-radius": "50%",
        background: props.color,
      }}
    />
    {props.label}
  </span>
);

export default ResourceGraph;
